import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllAccountAnalyticsForAdminV2.graphql';
import { Query, QueryallAccountAnalyticsForAdminV2Args } from '../../__generated__/globalTypes';

export const useAllAccountAnalyticsForAdminV2Query = (
  options?: QueryHookOptions<Query, QueryallAccountAnalyticsForAdminV2Args>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallAccountAnalyticsForAdminV2Args>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllAccountAnalyticsForAdminV2LazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallAccountAnalyticsForAdminV2Args>
): QueryResult<Query, QueryallAccountAnalyticsForAdminV2Args> & {
  getAllAccountAnalyticsForAdminV2: LazyQueryExecFunction<Query, QueryallAccountAnalyticsForAdminV2Args>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllAccountAnalyticsForAdminV2, result] = useLazyQuery<Query, QueryallAccountAnalyticsForAdminV2Args>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getAllAccountAnalyticsForAdminV2, ...result };
};

export const useAllAccountAnalyticsForAdminV2PromiseQuery = () => {
  const client = useApolloClient();

  const getAllAccountAnalyticsForAdminV2 = useCallback(
    (options?: Omit<QueryOptions<QueryallAccountAnalyticsForAdminV2Args, Query>, 'query'>) =>
      client.query<Query, QueryallAccountAnalyticsForAdminV2Args>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllAccountAnalyticsForAdminV2 };
};
// Suspense Query Hooks
export const useAllAccountAnalyticsForAdminV2SuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryallAccountAnalyticsForAdminV2Args>
) => {
  const result = useSuspenseQuery<Query, QueryallAccountAnalyticsForAdminV2Args>(QUERY, {
    ...options
  });

  return result;
};
